import {
  DynamicFieldProperty_Enum,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { LinkData, TEMPLATE_TYPE_LINK_MAP, useGetDefaultTemplate } from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ComponentProps } from '@frontend/types';
import { useModalControl } from '@frontend/design-system';
import { FormsTemplateModal } from '../components/forms-template-modal';
import { UseTemplateFlowArgs, UseTemplateFlowResult } from '../types';

type UseFormsTemplateFlowArgs = UseTemplateFlowArgs & {
  disabled?: boolean;
  personPhone: string;
};

export const useFormsTemplateFlow = ({
  disabled,
  groupId,
  personId,
  personPhone,
  renderTemplate,
  onOpenSettings,
}: UseFormsTemplateFlowArgs): UseTemplateFlowResult<ComponentProps<typeof FormsTemplateModal>> => {
  const { t } = useTranslation('inbox-templates');
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const { getLocationName } = useAppScopeStore();
  const modalControl = useModalControl();

  return {
    popoverItem: !!personId
      ? {
          iconName: 'forms',
          label: t('Forms'),
          trackingId: `${InboxPrefixes.Thread}-forms-template-button`,
          onClick: !disabled ? modalControl.openModal : undefined,
        }
      : undefined,
    modalProps: {
      ...modalControl.modalProps,
      groupId,
      personId,
      personPhone,
      onInsert: ({ template, appointmentId, appointmentAt, submissionId, formOrPacketLink }) => {
        const resolvedTemplate = template ?? getDefaultTemplate({ type: TemplateType_Slug.MANUAL_FORMS_MESSAGE });
        if (resolvedTemplate) {
          const formRelatedType = Object.keys(TEMPLATE_TYPE_LINK_MAP).includes(resolvedTemplate.templateTypeSlug)
            ? TEMPLATE_TYPE_LINK_MAP[resolvedTemplate.templateTypeSlug as keyof typeof TEMPLATE_TYPE_LINK_MAP]
                ?.relatedType
            : undefined;
          const linkData: LinkData[] = [
            { link: formOrPacketLink, relatedId: submissionId, relatedType: formRelatedType },
          ];
          if (appointmentId)
            linkData.push({
              link: formOrPacketLink,
              relatedId: appointmentId,
              relatedType: RelatedType.RELATED_TYPE_APPOINTMENT,
            });
          renderTemplate({
            template: resolvedTemplate,
            linkData,
            additionalBindings: {
              [DynamicFieldProperty_Enum.FORMS_LINK]: formOrPacketLink,
              [DynamicFieldProperty_Enum.SITE_NAME]: getLocationName(groupId),
              ...(appointmentAt && { [DynamicFieldProperty_Enum.EVENT_AT]: appointmentAt }),
            },
          });
          modalControl.closeModal();
        }
      },
      onOpenSettings,
    },
  };
};
